import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoalsContext } from '../State/GoalsContext';
import { IndicatorsContext } from '../State/IndicatorsContext';
import { goalTypeToUrl } from '../utils/goalTypeUtils';
import { route } from '../utils/apiRoutes';
import { transformTargetPreview } from '../utils/transformApiResponse';
import { searchPortalContent } from '../utils/portalSearch';
import './HeaderSearch.css';

/**
 * Sticky header search.
 * Results: goals + (UN targets | EU sub-themes) + indicators for the active portal (CP-1, D-11).
 */
const HeaderSearch = () => {
  const { goals, goalType } = useContext(GoalsContext);
  const { indicators, loading: indicatorsLoading } = useContext(IndicatorsContext);
  const navigate = useNavigate();
  const listId = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchTargets, setSearchTargets] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(false);

  // Clear when switching UN ↔ EU so results never mix portals.
  useEffect(() => {
    setQuery('');
    setOpen(false);
    setActiveIndex(-1);
  }, [goalType]);

  // Portal-scoped target / sub-theme catalogue for search (local — do not clobber TargetsContext).
  useEffect(() => {
    let cancelled = false;
    setTargetsLoading(true);
    setSearchTargets([]);

    (async () => {
      try {
        const batchUrl = route.targetsPreviewBatch(goalType, undefined, false);
        const res = await fetch(batchUrl);
        if (!res.ok) throw new Error(`targets ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data?.targets) ? data.targets : [];
        if (!cancelled) {
          setSearchTargets(list.map((preview) => transformTargetPreview(preview)).filter(Boolean));
        }
      } catch (err) {
        console.error('HeaderSearch: failed to load targets for search', err);
        if (!cancelled) setSearchTargets([]);
      } finally {
        if (!cancelled) setTargetsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [goalType]);

  const results = useMemo(
    () => searchPortalContent(query, goals, indicators, searchTargets, goalType, 12),
    [query, goals, indicators, searchTargets, goalType]
  );

  const portalLabel = goalType === 'eu' ? 'EU' : 'UN';
  const midLabel = goalType === 'eu' ? 'sub-themes' : 'targets';
  const placeholder = `Search ${portalLabel} goals, ${midLabel} & indicators…`;
  const catalogueLoading = indicatorsLoading || targetsLoading;

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [close]);

  const goToResult = useCallback(
    (result) => {
      if (!result) return;
      const base = `/${goalTypeToUrl(goalType)}`;
      if (result.kind === 'goal') {
        navigate(`${base}/goal/${result.id}/indicators`);
      } else if (result.kind === 'target') {
        const gid = result.goalId;
        if (gid) {
          navigate(`${base}/goal/${gid}/targets/${result.id}`);
        } else {
          navigate(base);
        }
      } else {
        const gid = result.goalId;
        if (gid) {
          navigate(`${base}/goal/${gid}/indicators/${result.id}`);
        } else {
          navigate(`${base}/indicators-catalog`);
        }
      }
      setQuery('');
      close();
    },
    [close, goalType, navigate]
  );

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter') && query.trim().length >= 2) {
      setOpen(true);
    }
    if (e.key === 'Escape') {
      close();
      inputRef.current?.blur();
      return;
    }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = activeIndex >= 0 ? results[activeIndex] : results[0];
      goToResult(pick);
    }
  };

  const showPanel = open && query.trim().length >= 2;

  return (
    <div className="header-search" ref={rootRef} role="search">
      <label htmlFor="header-portal-search" className="visually-hidden">
        Search {portalLabel} SDG goals, {midLabel}, and indicators
      </label>
      <div className="header-search__field">
        <input
          ref={inputRef}
          id="header-portal-search"
          type="search"
          className="header-search__input"
          placeholder={placeholder}
          value={query}
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={showPanel}
          aria-activedescendant={
            activeIndex >= 0 && results[activeIndex]
              ? `${listId}-opt-${activeIndex}`
              : undefined
          }
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {query ? (
          <button
            type="button"
            className="header-search__clear"
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
              setActiveIndex(-1);
              inputRef.current?.focus();
            }}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        ) : null}
        <button
          type="button"
          className="header-search__submit"
          aria-label={`Search ${portalLabel} goals, ${midLabel}, and indicators`}
          onClick={() => {
            setOpen(true);
            if (results.length > 0) {
              goToResult(activeIndex >= 0 ? results[activeIndex] : results[0]);
            } else {
              inputRef.current?.focus();
            }
          }}
        >
          <i className="bi bi-search" aria-hidden="true" />
        </button>
      </div>

      {showPanel && (
        <div className="header-search__panel" id={listId} role="listbox">
          {results.length === 0 ? (
            <div className="header-search__empty">
              {catalogueLoading
                ? `Searching ${portalLabel} catalogue…`
                : `No ${portalLabel} matches for “${query.trim()}”`}
            </div>
          ) : (
            <ul className="header-search__list">
              {results.map((r, index) => (
                <li key={`${r.kind}-${r.id}`} role="none">
                  <button
                    type="button"
                    id={`${listId}-opt-${index}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`header-search__option${index === activeIndex ? ' is-active' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => goToResult(r)}
                  >
                    <span className="header-search__kind">{r.secondary}</span>
                    <span className="header-search__label">{r.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="header-search__footer">
            Searching {portalLabel} SDG only
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
